<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const { t } = useI18n();

const isLoading = ref(true);
const showModal = ref(false);
const editingIntegration = ref(null);
const isSaving = ref(false);
const isTesting = ref(false);
const testResult = ref(null);

const integrations = computed(
  () => store.getters['liaIntegrations/getIntegrations']
);
const providers = computed(() => store.getters['liaIntegrations/getProviders']);

const formData = ref({
  name: '',
  provider: '',
  credentials: {
    api_url: '',
    api_token: '',
    client_id: '',
    client_secret: '',
  },
  settings: {},
  active: true,
});

const providerFields = {
  ixc: [
    {
      key: 'api_url',
      label: 'URL da API',
      placeholder: 'https://ixc.suaempresa.com.br/webservice/v1',
      required: true,
    },
    {
      key: 'api_token',
      label: 'Token da API',
      placeholder: 'Seu token de acesso',
      required: true,
      type: 'password',
    },
  ],
  mk_solutions: [
    {
      key: 'api_url',
      label: 'URL da API',
      placeholder: 'https://api.mksolutions.com.br',
      required: true,
    },
    {
      key: 'api_token',
      label: 'Token da API',
      placeholder: 'Seu token de acesso',
      required: true,
      type: 'password',
    },
  ],
  hubsoft: [
    {
      key: 'api_url',
      label: 'URL da API',
      placeholder: 'https://api.hubsoft.com.br',
      required: true,
    },
    { key: 'client_id', label: 'Client ID', required: true },
    {
      key: 'client_secret',
      label: 'Client Secret',
      required: true,
      type: 'password',
    },
  ],
  custom: [
    {
      key: 'api_url',
      label: 'URL da API',
      placeholder: 'https://api.seuservico.com',
      required: true,
    },
    {
      key: 'api_token',
      label: 'Token/API Key',
      required: false,
      type: 'password',
    },
    { key: 'client_id', label: 'Client ID', required: false },
    {
      key: 'client_secret',
      label: 'Client Secret',
      required: false,
      type: 'password',
    },
  ],
};

const currentProviderFields = computed(() => {
  return providerFields[formData.value.provider] || [];
});

const providerIcons = {
  ixc: 'i-lucide-server',
  mk_solutions: 'i-lucide-database',
  hubsoft: 'i-lucide-cloud',
  notificame: 'i-lucide-bell',
  sgp: 'i-lucide-layout-grid',
  voalle: 'i-lucide-zap',
  custom: 'i-lucide-code',
};

const fetchData = async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('liaIntegrations/fetch'),
      store.dispatch('liaIntegrations/fetchProviders'),
    ]);
  } catch (error) {
    useAlert('Erro ao carregar integrações');
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchData);

const openModal = (integration = null) => {
  editingIntegration.value = integration;
  testResult.value = null;

  if (integration) {
    formData.value = {
      name: integration.name,
      provider: integration.provider,
      credentials: { ...integration.credentials },
      settings: { ...integration.settings },
      active: integration.active,
    };
  } else {
    formData.value = {
      name: '',
      provider: '',
      credentials: {
        api_url: '',
        api_token: '',
        client_id: '',
        client_secret: '',
      },
      settings: {},
      active: true,
    };
  }

  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingIntegration.value = null;
  testResult.value = null;
};

const saveIntegration = async () => {
  isSaving.value = true;
  try {
    if (editingIntegration.value) {
      await store.dispatch('liaIntegrations/update', {
        id: editingIntegration.value.id,
        ...formData.value,
      });
      useAlert('Integração atualizada com sucesso');
    } else {
      await store.dispatch('liaIntegrations/create', formData.value);
      useAlert('Integração criada com sucesso');
    }
    closeModal();
  } catch (error) {
    useAlert(error.message || 'Erro ao salvar integração');
  } finally {
    isSaving.value = false;
  }
};

const testConnection = async () => {
  if (!editingIntegration.value) return;

  isTesting.value = true;
  testResult.value = null;
  try {
    const result = await store.dispatch(
      'liaIntegrations/testConnection',
      editingIntegration.value.id
    );
    testResult.value = {
      success: true,
      message: result.message || 'Conexão bem sucedida!',
      availableFeatures: result.details?.available_features || null,
    };
  } catch (error) {
    testResult.value = {
      success: false,
      message: error.message || 'Falha na conexão',
    };
  } finally {
    isTesting.value = false;
  }
};

// Category labels for displaying features
const categoryLabels = {
  cadastro: 'Cadastro',
  financeiro: 'Financeiro',
  suporte: 'Suporte Técnico',
  comercial: 'Comercial',
};

// Icons map for features
const featureIcons = {
  users: 'i-lucide-users',
  'file-text': 'i-lucide-file-text',
  'alert-circle': 'i-lucide-alert-circle',
  'check-circle': 'i-lucide-check-circle',
  calendar: 'i-lucide-calendar',
  wifi: 'i-lucide-wifi',
  'refresh-cw': 'i-lucide-refresh-cw',
  'help-circle': 'i-lucide-help-circle',
  clipboard: 'i-lucide-clipboard',
  'plus-circle': 'i-lucide-plus-circle',
  file: 'i-lucide-file',
  'message-square': 'i-lucide-message-square',
  'shopping-cart': 'i-lucide-shopping-cart',
};

const deleteIntegration = async integration => {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (
    !confirm(
      `Tem certeza que deseja excluir a integração "${integration.name}"?`
    )
  )
    return;

  try {
    await store.dispatch('liaIntegrations/delete', integration.id);
    useAlert('Integração excluída com sucesso');
  } catch (error) {
    useAlert(error.message || 'Erro ao excluir integração');
  }
};

const getProviderName = providerId => {
  const provider = providers.value.find(p => p.id === providerId);
  return provider?.name || providerId;
};

const isFormValid = computed(() => {
  if (!formData.value.name || !formData.value.provider) return false;

  const requiredFields = currentProviderFields.value.filter(f => f.required);
  return requiredFields.every(field =>
    formData.value.credentials[field.key]?.trim()
  );
});
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-xl font-semibold text-n-slate-12">Integrações</h2>
        <p class="text-sm text-n-slate-11 mt-1">
          Configure as integrações com sistemas externos (IXC, MK Solutions,
          etc.)
        </p>
      </div>
      <button
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors"
        @click="openModal()"
      >
        <span class="i-lucide-plus size-4" />
        Nova Integração
      </button>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-12">
      <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
    </div>

    <!-- Empty State -->
    <div v-else-if="integrations.length === 0" class="text-center py-12">
      <div
        class="w-16 h-16 rounded-2xl bg-n-alpha-2 flex items-center justify-center mx-auto mb-4"
      >
        <span class="i-lucide-plug size-8 text-n-slate-11" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        Nenhuma integração configurada
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        Configure integrações com IXC, MK Solutions ou outros sistemas
      </p>
      <button
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors mx-auto"
        @click="openModal()"
      >
        <span class="i-lucide-plus size-4" />
        Criar primeira integração
      </button>
    </div>

    <!-- Integrations Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="integration in integrations"
        :key="integration.id"
        class="bg-n-solid-2 rounded-xl p-5 border border-n-weak hover:border-n-strong transition-colors"
      >
        <div class="flex items-start justify-between mb-4">
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center"
              :class="
                integration.active
                  ? 'bg-woot-100 dark:bg-woot-900/50'
                  : 'bg-n-alpha-2'
              "
            >
              <span
                :class="providerIcons[integration.provider] || 'i-lucide-plug'"
                class="size-5"
                :style="{ color: integration.active ? '#1f93ff' : '' }"
              />
            </div>
            <div>
              <h3 class="font-semibold text-n-slate-12">
                {{ integration.name }}
              </h3>
              <p class="text-xs text-n-slate-11">
                {{ getProviderName(integration.provider) }}
              </p>
            </div>
          </div>
          <span
            class="px-2 py-0.5 text-xs font-medium rounded-full"
            :class="
              integration.active
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{ integration.active ? 'Ativo' : 'Inativo' }}
          </span>
        </div>

        <div class="text-xs text-n-slate-11 mb-4">
          <p v-if="integration.credentials?.api_url" class="truncate">
            {{ integration.credentials.api_url }}
          </p>
        </div>

        <div class="flex gap-2">
          <button
            class="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg transition-colors"
            @click="openModal(integration)"
          >
            <span class="i-lucide-settings size-4" />
            Configurar
          </button>
          <button
            class="p-2 text-n-slate-11 hover:text-red-500 bg-n-alpha-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            @click="deleteIntegration(integration)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div class="absolute inset-0 bg-black/50" @click="closeModal" />
        <div
          class="relative bg-n-solid-1 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto"
        >
          <!-- Modal Header -->
          <div
            class="sticky top-0 bg-n-solid-1 px-6 py-4 border-b border-n-weak flex items-center justify-between"
          >
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ editingIntegration ? 'Editar Integração' : 'Nova Integração' }}
            </h3>
            <button
              class="p-2 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors"
              @click="closeModal"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Modal Content -->
          <div class="p-6 space-y-5">
            <!-- Name -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                Nome da integração *
              </label>
              <input
                v-model="formData.name"
                type="text"
                placeholder="Ex: IXC Produção"
                class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>

            <!-- Provider -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                Provedor *
              </label>
              <select
                v-model="formData.provider"
                class="w-full px-4 py-2.5 pr-10 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 appearance-none select-caret"
                :disabled="!!editingIntegration"
              >
                <option value="">Selecione...</option>
                <option
                  v-for="provider in providers"
                  :key="provider.id"
                  :value="provider.id"
                >
                  {{ provider.name }}
                </option>
              </select>
            </div>

            <!-- Provider-specific fields -->
            <template v-if="formData.provider">
              <div v-for="field in currentProviderFields" :key="field.key">
                <label class="block text-sm font-medium text-n-slate-12 mb-2">
                  {{ field.label }} {{ field.required ? '*' : '' }}
                </label>
                <input
                  v-model="formData.credentials[field.key]"
                  :type="field.type || 'text'"
                  :placeholder="field.placeholder"
                  class="w-full px-4 py-2.5 border border-n-weak rounded-lg bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-woot-500"
                />
              </div>
            </template>

            <!-- Active Toggle -->
            <div
              class="flex items-center justify-between p-4 bg-n-alpha-1 rounded-lg"
            >
              <div>
                <p class="font-medium text-n-slate-12">Integração ativa</p>
                <p class="text-xs text-n-slate-11">
                  Desative para pausar temporariamente
                </p>
              </div>
              <button
                type="button"
                class="relative w-11 h-6 rounded-full transition-colors"
                :class="formData.active ? 'bg-woot-500' : 'bg-n-alpha-3'"
                @click="formData.active = !formData.active"
              >
                <span
                  class="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
                  :class="formData.active ? 'translate-x-5' : 'translate-x-0.5'"
                />
              </button>
            </div>

            <!-- Test Result -->
            <div
              v-if="testResult"
              class="rounded-lg border"
              :class="
                testResult.success
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              "
            >
              <div class="flex items-center gap-2 p-4">
                <span
                  :class="
                    testResult.success
                      ? 'i-lucide-check-circle text-green-600'
                      : 'i-lucide-x-circle text-red-600'
                  "
                  class="size-5"
                />
                <p
                  class="text-sm font-medium"
                  :class="
                    testResult.success
                      ? 'text-green-700 dark:text-green-400'
                      : 'text-red-700 dark:text-red-400'
                  "
                >
                  {{ testResult.message }}
                </p>
              </div>

              <!-- Available Features -->
              <div
                v-if="testResult.success && testResult.availableFeatures"
                class="border-t border-green-200 dark:border-green-800 p-4"
              >
                <p
                  class="text-xs font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center gap-1"
                >
                  <span class="i-lucide-sparkles size-3.5" />
                  Recursos Disponíveis
                </p>

                <div class="space-y-3">
                  <template
                    v-for="(features, category) in testResult.availableFeatures"
                    :key="category"
                  >
                    <div v-if="features && features.length > 0">
                      <p
                        class="text-xs font-medium text-green-700 dark:text-green-400 mb-1.5"
                      >
                        {{ categoryLabels[category] || category }}
                      </p>
                      <div class="flex flex-wrap gap-1.5">
                        <span
                          v-for="feature in features"
                          :key="feature.name"
                          class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md"
                          :class="
                            feature.available
                              ? 'bg-green-100 dark:bg-green-800/50 text-green-700 dark:text-green-300'
                              : 'bg-yellow-100 dark:bg-yellow-800/50 text-yellow-700 dark:text-yellow-300'
                          "
                        >
                          <span
                            :class="
                              featureIcons[feature.icon] || 'i-lucide-circle'
                            "
                            class="size-3"
                          />
                          {{ feature.name }}
                          <span
                            v-if="!feature.available"
                            class="i-lucide-alert-triangle size-3"
                            title="Permissão limitada"
                          />
                        </span>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div
            class="sticky bottom-0 bg-n-solid-1 px-6 py-4 border-t border-n-weak flex justify-between"
          >
            <button
              v-if="editingIntegration"
              type="button"
              class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg transition-colors disabled:opacity-50"
              :disabled="isTesting"
              @click="testConnection"
            >
              <span
                v-if="isTesting"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span v-else class="i-lucide-zap size-4" />
              {{ isTesting ? 'Testando...' : 'Testar Conexão' }}
            </button>
            <div v-else />

            <div class="flex gap-3">
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors"
                @click="closeModal"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors disabled:opacity-50"
                :disabled="!isFormValid || isSaving"
                @click="saveIntegration"
              >
                <span
                  v-if="isSaving"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                {{ isSaving ? 'Salvando...' : 'Salvar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.select-caret {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none' stroke='rgb%28110,111,115%29' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M2 2.5 6 6 10 2.5'/></svg>");
  background-origin: content-box;
  background-position: right -1rem center;
  background-repeat: no-repeat;
  background-size: 10px 7px;
}
</style>
